import { styled } from '@mui/material';

import { memo } from 'react';

import { useI18n } from '@app-velocity/i18n';
import { Tooltip } from '@app-velocity/ui';

import { SinglePath, SinglePathView } from '../../types';
import { ScreenConfidence } from '../ScreenConfidence';
import { OuterSlideContainer, PathScreensScroll } from '../PathScreensScroll';
import { ViewPreview } from '../ViewPreview';

const StyledWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected?: boolean }>(({ theme, selected }) => ({
  border: selected ? '1px solid #e3e3e3' : '1px solid transparent',
  borderRadius: theme.spacing(4),
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  marginBottom: selected ? theme.spacing(-4) : 0,
  marginTop: selected ? theme.spacing(-4) : 0,
  padding: selected ? theme.spacing(4) : 0,
  paddingBottom: selected ? `calc(${theme.spacing(9)} - 1px)` : 0,
  paddingTop: selected ? theme.spacing(4) : 0,
  borderBottom: selected ? '1px solid #fff' : 'none',
}));

const StyledSlideButton = styled('button')(({ theme }) => ({
  flex: '0 0 200px',
  height: '250px',
  display: 'block',
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
  border: 'none',
  borderRadius: theme.spacing(1),
  boxSizing: 'border-box',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  transition: 'transform 0.15s ease-in-out',
}));

export type PathRunScreensProps = {
  path: SinglePath;
  views: SinglePathView[];
  selectedViewIndex: number | null;
  hasDetails: boolean;
  onViewClick: (index: number) => void;
};

export const PathRunScreens = memo(
  ({
    path,
    views,
    selectedViewIndex,
    hasDetails,
    onViewClick,
  }: PathRunScreensProps) => {
    const { t } = useI18n();

    return (
      <PathScreensScroll itemCount={views.length}>
        {({ getItemRef, shouldIgnoreClick }) =>
          views.map((view, index) => (
            <OuterSlideContainer
              key={view.checksum || index}
              ref={getItemRef(index)}
            >
              <StyledWrapper selected={selectedViewIndex === index}>
                <Tooltip
                  title={t(
                    selectedViewIndex === index
                      ? 'prototypes:report.paths.hide_details'
                      : 'prototypes:report.paths.see_details'
                  )}
                >
                  <StyledSlideButton
                    type="button"
                    onClick={() => {
                      if (!hasDetails) return;
                      if (shouldIgnoreClick()) return;
                      onViewClick(index);
                    }}
                  >
                    <ViewPreview
                      persona={path.persona || 'default'}
                      personaConfig={path.personaConfig}
                      size="normal"
                      pageId={view.pageId}
                      partials={view.partials}
                      clicks={view.clicks}
                      image={view.image}
                      width={view.width}
                      height={view.height}
                    />
                  </StyledSlideButton>
                </Tooltip>
                {selectedViewIndex !== index ? (
                  <ScreenConfidence confidence={view.confidence || []} />
                ) : null}
              </StyledWrapper>
            </OuterSlideContainer>
          ))
        }
      </PathScreensScroll>
    );
  }
);
